import { useEffect, useState } from 'react'

import dayjs from 'dayjs'
import { NavigateFunction } from 'react-router-dom'

import { axiosInstance } from '~/api/base'

import {
  IStatementContext,
  IStatementDataSource,
  IStatementEvent
} from './context'
import {
  ClusterInfo,
  StmtPlanDetailParams,
  DetailUrlState,
  StmtListParams,
  StmtPlansParams,
  StatementModel,
  SlowQueryListParams,
  SlowQueryModel,
  SlowQueryDetailUrlState
} from './type'

const getNgmPath = () => `/ngm/api/v1`
const getNgmHeader = ({
  orgExternalId,
  projectId,
  clusterId,
  clusterProvider,
  clusterRegion,
  deployType
}: ClusterInfo) => ({
  'x-deploy-type': deployType || 'dedicated',
  'x-provider': clusterProvider,
  'x-region': clusterRegion,
  'x-org-id': orgExternalId,
  ...(projectId
    ? {
        'x-project-id': projectId
      }
    : {}),
  'x-cluster-id': clusterId
})

const sharedMap = new Map<string, any>()
const DETAIL_FROM_HREF = 'detail_from_url'

class DataSource implements IStatementDataSource {
  apiPrefix: string
  apiHeader: any

  constructor(clusterInfo: ClusterInfo) {
    this.apiPrefix = getNgmPath()
    this.apiHeader = getNgmHeader(clusterInfo)
  }

  getStmtKinds(): Promise<string[]> {
    return axiosInstance
      .get(`${this.apiPrefix}/statements/stmt_types`, {
        headers: this.apiHeader
      })
      .then((res) => res.data)
  }

  getStmtList(reqParams: StmtListParams): Promise<StatementModel[]> {
    const { begin_time, end_time, search: text = '' } = reqParams

    const params = new URLSearchParams({ begin_time, end_time, text })
    reqParams.kinds.forEach((kind) => {
      params.append('stmt_types', kind)
    })
    params.set(
      'fields',
      'digest_text,sum_latency,avg_latency,max_latency,min_latency,exec_count,plan_count,sum_ru,avg_ru'
    )

    // save to share with other API calls
    sharedMap.set('begin_time', begin_time)
    sharedMap.set('end_time', end_time)

    return axiosInstance
      .get<StatementModel[]>(
        `${this.apiPrefix}/statements/list?${params.toString()}`,
        {
          headers: this.apiHeader
        }
      )
      .then((res) => res.data)
  }

  stmtExists(reqParams: StmtListParams): Promise<boolean> {
    const { begin_time, end_time, search: text = '' } = reqParams

    const params = new URLSearchParams({ begin_time, end_time, text })
    reqParams.kinds.forEach((kind) => {
      params.append('stmt_types', kind)
    })
    params.set('fields', 'digest_text')

    return axiosInstance
      .get<StatementModel[]>(
        `${this.apiPrefix}/statements/list?${params.toString()}`,
        {
          headers: this.apiHeader
        }
      )
      .then((res) => res.data.length > 0)
  }

  getStmtPlans(reqParams: StmtPlansParams): Promise<StatementModel[]> {
    return axiosInstance
      .get<StatementModel[]>(`${this.apiPrefix}/statements/plans`, {
        params: reqParams,
        headers: this.apiHeader
      })
      .then((res) => res.data)
  }

  getStmtPlanDetail(reqParams: StmtPlanDetailParams): Promise<StatementModel> {
    let { digest, schema_name, begin_time, end_time, plans = [] } = reqParams
    const params = new URLSearchParams({
      digest,
      ...(schema_name ? { schema_name } : {}),
      begin_time,
      end_time
    })
    plans.forEach((plan) => {
      params.append('plans', plan)
    })

    return axiosInstance
      .get<StatementModel>(
        `${this.apiPrefix}/statements/plan/detail?${params.toString()}`,
        {
          headers: this.apiHeader
        }
      )
      .then((res) => res.data)
  }

  getStmtSlowQueryList(
    reqParams: SlowQueryListParams
  ): Promise<SlowQueryModel[]> {
    let { begin_time, end_time, digest, plans, orderBy, desc } = reqParams

    // currently, slow query log only support max 3 hours duration
    // if the duration exceeds 3 hours, we will get 400 error
    // we can revert this fix after the 3 hours duration limitation is removed
    let et = parseInt(end_time)
    let bt = parseInt(begin_time)
    if (et - bt > 3 * 60 * 60) {
      bt = et - 3 * 60 * 60
      begin_time = `${bt}`
    }

    const params = new URLSearchParams({
      begin_time,
      end_time,
      digest,
      fields: 'query,timestamp,query_time,memory_max',
      limit: '100',
      orderBy,
      desc: desc + ''
    })
    plans.forEach((plan) => {
      params.append('plans', plan)
    })

    return axiosInstance
      .get<SlowQueryModel[]>(
        `${this.apiPrefix}/slow_query/list?${params.toString()}`,
        {
          headers: this.apiHeader
        }
      )
      .then((res) => res.data)
  }
}

class EventHandler implements IStatementEvent {
  constructor(
    public clusterId: string,
    public orgId: string,
    public navigate: NavigateFunction
  ) {}

  gotoDetail = (params: DetailUrlState) => {
    sharedMap.set(
      DETAIL_FROM_HREF,
      window.location.hash.replace('#', '') + window.location.search
    )
    console.log('gotoDetail params', sharedMap.get(DETAIL_FROM_HREF))

    const urlParams = new URLSearchParams()
    if (params.digest) urlParams.set('digest', params.digest)
    if (params.schema_name) urlParams.set('schema_name', params.schema_name)
    urlParams.set(
      'begin_time',
      params.begin_time || sharedMap.get('begin_time') || ''
    )
    urlParams.set(
      'end_time',
      params.end_time || sharedMap.get('end_time') || ''
    )
    if (params.plans) urlParams.set('plans', params.plans)
    if (params.slowquery_sort)
      urlParams.set('slowquery_sort', params.slowquery_sort)

    this.navigate(
      `/orgs/${this.orgId}/clusters/${
        this.clusterId
      }/sql-repository/detail?${urlParams.toString()}`
    )
  }

  backFromDetail = () => {
    const fromHref = sharedMap.get(DETAIL_FROM_HREF)
    console.log('backFromDetail fromHref', fromHref)
    if (fromHref) {
      this.navigate(fromHref)
    } else {
      this.navigate(
        `/orgs/${this.orgId}/clusters/${this.clusterId}/sql-repository`
      )
    }
    sharedMap.delete(DETAIL_FROM_HREF)
  }

  gotoSlowQueryDetail = (params: SlowQueryDetailUrlState) => {
    const urlParams = new URLSearchParams()
    if (params.digest) urlParams.set('digest', params.digest)
    if (params.connection_id)
      urlParams.set('connection_id', params.connection_id)
    if (params.timestamp) urlParams.set('timestamp', params.timestamp)
    this.navigate(
      `/orgs/${this.orgId}/clusters/${
        this.clusterId
      }/slow-query/detail?${urlParams.toString()}`
    )
  }
}

export const getStatementContext: (
  info: ClusterInfo,
  navigate: NavigateFunction
) => IStatementContext = (info, navigate) => {
  return {
    cfg: { clusterId: info.clusterId, computed: info.computed },
    ds: new DataSource(info),
    event: new EventHandler(info.clusterId, info.orgId, navigate)
  }
}

export const useShouldShowSQLRepository = (info: ClusterInfo) => {
  const [shouldShow, setShouldShow] = useState(false)
  useEffect(() => {
    if (shouldShow) return
    const ds = new DataSource(info)
    const endTime = dayjs().unix().toString()
    const beginTime = dayjs().subtract(180, 'minutes').unix().toString()
    ds.stmtExists({
      begin_time: beginTime,
      end_time: endTime,
      kinds: [],
      search: ''
    }).then((res) => {
      setShouldShow(res)
    })
  }, [info])
  return shouldShow
}
